import {
  WSAlert,
  WSBreadcrumbsItem,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSPill,
  WSPoster,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IPayerResponse,
  IRequirementResponse,
  PayeeTaxDocumentSharePermission,
  PayerPayeeEngagementStatus,
  PayerPayeeStatus
} from "@wingspanhq/payments/dist/interfaces";
import { queriesPayerRows, queriesPayers } from "@query";
import { selectorPayerEngagementsFromV3 } from "@selectors/selectorPayerEngagementsFromV3";
import { selectorPayerResponseFromV3 } from "@selectors/selectorPayerResponseFromV3";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import React, { useCallback, useState } from "react";
import { Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import { Tabs } from "../../../../components/Tabs";
import { useQueryMemberPrivateFiles } from "../../../../query/files/queries";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryIntegrationsQuickbooks } from "../../../../query/integrations/queries";
import { useQueryRequirementsList } from "../../../../query/requirements/queries/useQueryRequirementsList";
import { WSQueries } from "../../../../query/WSQuery";
import { useModalShareTaxInformationWithPayers } from "../../../../shared/components/ModalShareTaxInformationWithPayers/useModalShareTaxInformationWithPayers";
import { ResourceHistory } from "../../../../shared/components/ResourceHistory";
import { selectorPayerPayeeEngagements } from "../../../Payees/selectors/selectorPayerPayeeEngagements";
import { selectorExpiredRequirements } from "../../../Requirements/selectors/selectorExpiredRequirements";
import { selectorExpiringRequirements } from "../../../Requirements/selectors/selectorExpiringRequirements";
import { AutoPay } from "../../components/AutoPay";
import { ClientInfoPanel } from "../../components/ClientInfoPanel";
import { DocumentsSection } from "../../components/DocumentsSection";
import { DocumentsSectionType } from "../../components/DocumentsSection/types";
import { useEditClientModal } from "../../components/EditClientModal/useEditClientModal";
import { EngagementsTable } from "../../components/EngagementsTable";
import { PayerIntegrationWidget } from "../../components/IntegrationsSection/PayerIntegrationWidget";
import { usePayerHistoryResources } from "../../hooks/usePayerHistoryResources";
import {
  getPayerDetailsRequirementsUrl,
  getPayerDetailsUrl,
  getPayerHistoryUrl,
  PAYER_DETAILS_PATH,
  PAYER_DETAILS_REQUIREMENTS_PATH,
  PAYERS_PATH
} from "../../paths";
import { useArchiveClientModal } from "./ArchiveClientModal";
import { useRestoreClientModal } from "./RestoreClientModal";
import { RecentInvoicesWidget } from "./RecentInvoicesWidget";
import { WireTransferSection } from "./WireTransferSection";
import { PayerPayeeDetailsRequirements } from "../../../Requirements/components/PayerPayeeDetailsRequirements";
import { RequirementContextType } from "../../../Requirements/types";

// ============================================================================
// v3 migration — accepted breakage (directive: "not a single v1 endpoint may be
// called on this screen"; user-confirmed 2026-07-22).
//
// Everything the Client Details screen needs to READ (`getPayer`) and WRITE
// (edit / archive / share-tax-info) is on v3. Assignments now reads v3 too
// (`searchPayerRows` + `selectorPayerEngagementsFromV3`, see below — the row
// carries per-engagement requirements/eligibility the lean CRUD
// `PayerEngagement` doesn't). The remaining widgets commented out below hit v1
// users-service endpoints whose data has NO usable v3 source today:
//   - Requirements tab → v1 GET /payments/requirement (+ v1 requirement-action
//                        mutations); by design there is no payer-nested v3
//                        requirements endpoint.
//   - Documents        → v1 GET /files/member/private (no v3 files registry).
//   - QuickBooks widget→ v1 /experiments + /integrations/quickbooks; the mapping
//                        lives on v3 AccountingConnection, not the Payer.
//   - AutoPay          → v1 user/member profile; deliberately cut from the payee's
//                        Payer surface (design 2026-06-10).
//   - Resource history → v1 GET /v2/resource-events; v3 audit-logs not yet wired.
//
// On a v3 payer id every one of these already returned empty (their data never
// resolved), so commenting them out is not a functional regression — it just
// removes the dead v1 traffic. They are preserved as comments for re-enablement
// once a v3 source ships. See V3_MIGRATION_TRACKER.md · /v3/payments/payers.
// ============================================================================

export const RouteClientDetails: React.FC = () => {
  const history = useHistory();

  const match = useRouteMatch<{ payerId: string }>();
  const payerId = match.params.payerId;
  const modalFormW9PayerConsent = useModalShareTaxInformationWithPayers();

  // v3 read: getPayer, adapted to the v1 IPayerResponse shape the widgets consume.
  const queryClient = queriesPayers.getPayer.useQuery(
    { payerId },
    { select: selectorPayerResponseFromV3 }
  );

  // v3 read: the denormalized search row, adapted to the v1
  // IPayerEngagementResponse[] shape the Assignments list + its selectors
  // consume unchanged (see selectorPayerEngagementsFromV3).
  const queryPayerRows = queriesPayerRows.searchPayerRows.useQuery({
    filter: { id: { eq: payerId } }
  });
  const payerEngagements = selectorPayerEngagementsFromV3(
    queryPayerRows.data?.data?.[0]
  );

  // --- v3-migration accepted breakage: v1 reads removed (see banner above) ---
  // const userId = useUserId();
  // const queryRequirements = useQueryRequirementsList({
  //   filter: {
  //     payerId,
  //     payeeId: userId,
  //     payerPayeeEngagementIds: { "!=": "null" }
  //   },
  //   sort: { createdAt: "asc" },
  //   page: { size: 99 }
  // });
  // const queryArchivedRequirements = useQueryRequirementsList({
  //   filter: {
  //     payerId,
  //     payeeId: userId,
  //     payerPayeeEngagementIds: "null"
  //   },
  //   sort: { createdAt: "asc" },
  //   page: { size: 99 }
  // });
  // const qDocuments = useQueryMemberPrivateFiles({
  //   filter: { hidden: true, tags: { in: [`payer:${payerId}`] } }
  // });
  // const queryFeatureFlags = useFeatureFlags();
  // const queryQBO = useQueryIntegrationsQuickbooks();
  // const historyResources = usePayerHistoryResources(payerId);

  const [showArchived, setShowArchived] = useState<boolean>(false);

  const archiveClientModal = useArchiveClientModal();
  const restoreClientModal = useRestoreClientModal();
  const editClientModal = useEditClientModal();

  const handleCreateInvoice = useCallback(() => {
    // v3-migration accepted breakage: the single-assignment auto-select shortcut
    // relied on the v1 payer-engagements read (503 on v3); prefill the client
    // email only. The invoice-create flow itself is a separate route.
    history.push("/member/invoices/create", {
      email: queryClient?.data?.user?.email
    });
  }, [history, queryClient.data]);

  const handleEditClient = () => {
    editClientModal.open({
      client: queryClient?.data as IPayerResponse
    });
  };

  // v3: restore reactivates the deactivated payer via `restorePayer` (WIN-15189).
  const handleRestoreClient = () => {
    restoreClientModal.open({
      client: queryClient?.data as IPayerResponse
    });
  };

  return (
    <WSPage>
      <WSQueries
        queries={{
          queryClient
        }}
      >
        {({ queryClientData: client }) => {
          const clientInfo = wsName({
            user: {
              email: client.user?.email as string,
              profile: client.user?.profile
            },
            member: client.member,
            payeeOwnedData: client.payeeOwnedData
          });

          const isInactive =
            client.payeeOwnedData.status === PayerPayeeStatus.Inactive;
          const isNotShared =
            client.payeeOwnedData?.shareTaxDocument !==
            PayeeTaxDocumentSharePermission.Allow;

          const filteredEngagements = selectorPayerPayeeEngagements(
            payerEngagements,
            false,
            true
          );
          const activeFilteredEngagements = filteredEngagements.filter(
            eng => eng.status === PayerPayeeEngagementStatus.Active
          );
          const inactiveFilteredEngagements = filteredEngagements.filter(
            eng => eng.status === PayerPayeeEngagementStatus.Inactive
          );

          // --- v3-migration accepted breakage: requirement-derived state
          // removed with its v1 reads (see banner at top of file) ---
          // const queryRequirementsData = queryRequirements.data ?? [];
          // const queryArchivedRequirementsData = queryArchivedRequirements.data ?? [];
          // const expiringRequirements = selectorExpiringRequirements(
          //   queryRequirementsData || []
          // );
          // const expiredRequirements = selectorExpiredRequirements(
          //   queryRequirementsData || []
          // );

          return (
            <>
              <WSPageToolbar
                breadcrumbs={[
                  {
                    label: "Clients",
                    onClick: () => {
                      history.push(PAYERS_PATH);
                    }
                  },
                  ...(isInactive
                    ? [
                        {
                          label: "Archived clients",
                          onClick: () => {
                            history.push(`${PAYERS_PATH}/archived`);
                          }
                        } as WSBreadcrumbsItem
                      ]
                    : []),
                  {
                    label: clientInfo.getResolvedName()
                  }
                ]}
                // "Edit client" (updatePayer), "Archive" (deletePayer
                // soft-deactivate), and "Restore" (restorePayer, WIN-15189) are on
                // v3. Still gapped: "Create invoice" targets the v1 invoice-create
                // flow.
                actions={
                  isInactive
                    ? [
                        {
                          label: "Restore client",
                          icon: "repeat-circle",
                          onClick: () => {
                            handleRestoreClient();
                          }
                        }
                      ]
                    : [
                        {
                          icon: "plus",
                          label: "Create invoice",
                          buttonKind: "Primary",
                          onClick: handleCreateInvoice
                        },
                        {
                          icon: "edit",
                          label: "Edit client",
                          onClick: handleEditClient
                        },
                        {
                          icon: "archive",
                          label: "Archive client",
                          onClick: () => {
                            archiveClientModal.open({
                              client
                            });
                          }
                        }
                      ]
                }
              />
              <WSFlexBox.CenterY mb="XL">
                <WSText.Heading3 mr="L">
                  {clientInfo.getResolvedName()}
                </WSText.Heading3>
                {client.payeeOwnedData.status === PayerPayeeStatus.Inactive ? (
                  <WSPill theme="neutral" text="Archived" />
                ) : null}
              </WSFlexBox.CenterY>
              <WSList gap="L">
                {
                  // Registered but tax info not shared — the only alert with a v3
                  // source (Payer.taxInformationShare).
                  isNotShared ? (
                    <WSAlert
                      icon="alert"
                      size="M"
                      theme="warning"
                      title="Tax information not shared"
                      action={{
                        onClick: () => {
                          modalFormW9PayerConsent.open({
                            payerId: client.payerId
                          });
                        },
                        text: "Share tax information"
                      }}
                    >
                      <WSText.ParagraphSm weight="book" color="gray700">
                        You have not consented to share your tax information
                        with the client. Future payments to you may not be
                        completed until the information is shared.
                      </WSText.ParagraphSm>
                    </WSAlert>
                  ) : null
                }
                {/*
                  v3-migration accepted breakage: the remaining alert branches
                  (payments-eligibility pending, incomplete assignment requirements,
                  expiring/expired requirements) derive from the v1 engagements +
                  requirements reads, which have no v3 source on this screen.

                  areRequirementsCompleted && !hasEligibleEngagements
                    ? <WSAlert theme="warning" icon="alert" title="Payments eligibility pending">…</WSAlert>
                  : !areRequirementsCompleted
                    ? <WSAlert theme="warning" icon="alert" title="Assignment requirements incomplete">…</WSAlert>
                  : expiringRequirements.length > 0 || expiredRequirements.length > 0
                    ? (expired → "Requirements incomplete" + "Renew now";
                       expiring → "Requirements expiring soon" + "View requirements")
                  : null
                */}

                <WSGrid gutter="2XL">
                  <WSGrid.Item span={{ m: "8" }}>
                    <Tabs
                      mb="L"
                      tabs={[
                        {
                          label: "Summary",
                          path: getPayerDetailsUrl(payerId)
                        }
                        // v3-migration accepted breakage: the Requirements tab reads
                        // v1 /payments/requirement (no payer-nested v3 endpoint); its
                        // pill state derived from requirement completion.
                        // {
                        //   label: "Requirements",
                        //   path: getPayerDetailsRequirementsUrl(payerId),
                        //   pill: {
                        //     icon: true,
                        //     theme:
                        //       !isNotShared && areRequirementsCompleted
                        //         ? "success"
                        //         : "warning"
                        //   }
                        // }
                      ]}
                    />
                    <Switch>
                      <Route
                        path={PAYER_DETAILS_PATH}
                        exact
                        render={() => (
                          <WSList gap="L">
                            <WSElement>
                              <WSSectionToolbar title="Client information" />
                              <ClientInfoPanel client={client} />
                            </WSElement>
                            <WSElement>
                              <WSSectionToolbar title="Assignments" mb="M" />
                              {activeFilteredEngagements.length ? (
                                <EngagementsTable
                                  engagements={activeFilteredEngagements}
                                />
                              ) : (
                                <WSPanel>
                                  <WSPoster
                                    size="S"
                                    type="tasks"
                                    title="No assignments active"
                                    description="Once an assignment is added and active, it will appear here."
                                  />
                                </WSPanel>
                              )}
                            </WSElement>
                            {inactiveFilteredEngagements.length ? (
                              <WSElement>
                                <WSSectionToolbar
                                  title={
                                    showArchived ? "Archived assignments" : ""
                                  }
                                  mb="M"
                                  buttons={[
                                    {
                                      label: showArchived
                                        ? `Hide archived (${inactiveFilteredEngagements.length})`
                                        : `Show archived (${inactiveFilteredEngagements.length})`,
                                      onClick: () => {
                                        setShowArchived(v => !v);
                                      }
                                    }
                                  ]}
                                />
                                {showArchived ? (
                                  <EngagementsTable
                                    engagements={inactiveFilteredEngagements}
                                  />
                                ) : null}
                              </WSElement>
                            ) : null}
                          </WSList>
                        )}
                      />
                      {/*
                        v3-migration accepted breakage: the Requirements route
                        (PayerPayeeDetailsRequirements) is fed by the v1 requirements
                        reads + v1 requirement-action mutations; there is no
                        payer-nested v3 requirements endpoint. Commented out with the
                        Requirements tab above.

                        <Route
                          path={PAYER_DETAILS_REQUIREMENTS_PATH}
                          render={() => (
                            <PayerPayeeDetailsRequirements
                              hasEligibleEngagements={hasEligibleEngagements}
                              requirements={queryRequirementsData}
                              noActions={false}
                              archivedRequirements={queryArchivedRequirementsData}
                              contextType={RequirementContextType.Payer}
                              status={client.payerOwnedData.status}
                              goToRequirement={(requirement: IRequirementResponse) => {
                                history.push(
                                  `/member/payers/${client.payerId}/requirement/${requirement.requirementId}`
                                );
                              }}
                            />
                          )}
                        />
                      */}
                    </Switch>
                  </WSGrid.Item>

                  <WSGrid.Item span={{ m: "4" }}>
                    <WSList>
                      <RecentInvoicesWidget payerId={client.payerId} />
                      {/*
                        v3-migration accepted breakage — commented out to keep this
                        screen off v1. Each widget's data has no v3 source today
                        (see the banner at the top of this file):

                        <WireTransferSection client={client} />

                        {qDocuments?.data && qDocuments.data.length > 0 ? (
                          <DocumentsSection
                            isInactive={isInactive}
                            type={DocumentsSectionType.Client}
                            payerId={payerId}
                            payeeId={userId}
                          />
                        ) : null}

                        <PayerIntegrationWidget
                          isInactive={isInactive}
                          payerId={client.payerId}
                          customerId={client?.payeeOwnedData?.integration?.quickbooks?.customerId}
                          itemId={client?.payeeOwnedData?.integration?.quickbooks?.itemId}
                        />

                        <AutoPay client={client} />

                        <ResourceHistory
                          resources={historyResources}
                          onViewAll={() => { history.push(getPayerHistoryUrl(payerId)); }}
                        />
                      */}
                    </WSList>
                  </WSGrid.Item>
                </WSGrid>
              </WSList>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
